import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { WarsEvents, LoadingEvent } from "@playroomkit/pr-wars/events";
import { LoadingType } from "@playroomkit/pr-wars/utils";

const LoadingStateContext = createContext<{
  loadingMessage: string;
  showLoadingBar: boolean;
  percentage: number;
  type: LoadingType;
  gameLoadingVariant: number;
}>({
  loadingMessage: "",
  showLoadingBar: false,
  percentage: 0,
  type: LoadingType.MainMenu,
  gameLoadingVariant: 0,
});

export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [loadingMessage, setLoadingMessage] = useState("");
  const [showLoadingBar, setShowLoadingBar] = useState(false);
  const [percentage, setPercentage] = useState(0);
  const [type, setType] = useState<LoadingType>(LoadingType.MainMenu);
  const [gameLoadingVariant, setGameLoadingVariant] = useState(() =>
    Math.random() < 0.5 ? 0 : 1
  );
  const fakeTimerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    const handleLoadingMessage = (event: Event) => {
      const e = event as LoadingEvent;
      
      if (timeout) {
        clearTimeout(timeout);
      }

      if (e.percentage >= 100 || e.percentage < 0) {
        timeout = setTimeout(() => {
          isLoadingRef.current = false;
          setShowLoadingBar(false);
          setPercentage(0);
        }, 1200);

        // Set the percentage to 100 to show the loading bar until the timeout
        setPercentage(100);
        return;
      }

      setShowLoadingBar(true);
      setLoadingMessage(e.message ?? "");
      setType((e.loadingType ?? LoadingType.MainMenu) as LoadingType);

      if (!isLoadingRef.current) {
        setGameLoadingVariant((prev) => (prev === 0 ? 1 : 0));
        isLoadingRef.current = true;
      }

      if (e.fake) {
        setPercentage(e.percentage);
        handleFakeLoadingState();
      } else {
        setPercentage(e.percentage);
      }
    };

    const handleFakeLoadingState = () => {
      fakeTimerIntervalRef.current = setInterval(() => {
        setPercentage((prev) => (prev < 95 ? prev + 10 : 95));
      }, 250);
    };

    document.addEventListener(WarsEvents.LoadingEvent, handleLoadingMessage);

    return () => {
      document.removeEventListener(
        WarsEvents.LoadingEvent,
        handleLoadingMessage
      );
      if (fakeTimerIntervalRef.current) {
        clearInterval(fakeTimerIntervalRef.current);
      }
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  return (
    <LoadingStateContext.Provider value={{
      loadingMessage,
      showLoadingBar,
      percentage,
      type,
      gameLoadingVariant,
    }}>
      {children}
    </LoadingStateContext.Provider>
  );
};

export const useLoadingState = () => {
  const context = useContext(LoadingStateContext);
  if (!context) {
    throw new Error("useLoadingState must be used within a LoadingProvider");
  }
  return context;
};
